import { useNavigate } from 'react-router-dom';

import { t } from '../i18n/t';
import { En } from '../i18n/En';
import type { DebriefViewStatusSubscription$data } from '../__generated__/DebriefViewStatusSubscription.graphql';
import { citedErrors, severityLabel, type CitedErrorInput } from './debrief.ts';
import { GeneratedText } from './GeneratedText';
import { HearIt } from './HearIt';
import { kindLabel } from './practice.ts';
import { PracticeStatsRow, type PracticeOutcome } from './PracticeStatsRow';
import { preview } from './transcript.ts';

type Debrief = NonNullable<DebriefViewStatusSubscription$data['debriefStatus']>;
export type FocusArea = Debrief['focusAreas'][number];

/**
 * Re-exports the generated subscription type so callers
 * (e.g. tests that read `debrief.ts`) can stay import-free.
 */
export { DebriefViewStatusSubscription$data };
export type { Debrief };

export function FocusAreaWidget({
  area,
  language,
  outcomeMap,
}: {
  area: FocusArea;
  language: string | null | undefined;
  outcomeMap: Map<string, PracticeOutcome[]>;
}) {
  const navigate = useNavigate();
  const outcomes = outcomeMap.get(area.kind) ?? [];

  return (
    // Not a card-body: nested inside two more, it left a phone a few words a line.
    <li className="bg-base-100 rounded-box flex flex-col gap-1 p-3">
      <div className="flex items-center gap-2">
        <strong className="badge badge-outline badge-lg">{kindLabel(area.kind)}</strong>
      </div>
      <p className="text-base-content/60 mt-1">
        <GeneratedText language={language}>{area.why}</GeneratedText>
      </p>
      {outcomes.length > 0 && <PracticeStatsRow kind={area.kind} outcomes={outcomes} />}
      <button
        className="btn btn-sm btn-ghost mt-2 min-h-(--hit-min)"
        // `focus` alone. The `sessionId` this used to append fed the hub's
        // "mark correct" button, which is gone: an outcome is what a
        // conversation produced, not what a card asserts. The hub reads
        // `focus` as its kind filter.
        onClick={() => navigate(`/practice?focus=${area.kind}`)}
      >
        {t('focus-practise')}
      </button>
      <div className="divider my-2" />
      {area.turns.length > 0 && (
        <ul>
          {area.turns.map((turn) => (
            <li key={turn.id}>
              <p>
                <span dir="auto">{turn.transcript}</span>
              </p>
              {/* Null on every tutor turn — only the learner is recorded — so
                  a cited turn may have nothing to play back. */}
              {turn.audioUrl && <audio controls src={turn.audioUrl} className="w-full" />}
              <CitedErrors errors={turn.errors} />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * Every error recorded on a cited turn, not only those of the card's kind:
 * the two kinds come from different models and nothing makes them agree
 * (cited-errors §3). Nothing at all for a turn with none, because "no errors"
 * under a turn the card just cited would contradict it (§6).
 */
function CitedErrors({ errors }: { errors: readonly CitedErrorInput[] }) {
  const rows = citedErrors(errors);
  if (rows.length === 0) return null;
  return (
    <ul className="mt-2 flex flex-col gap-3">
      {rows.map((e) => (
        <li key={e.id} className="flex flex-col gap-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            {/* The struck pair read aloud is two bare phrases, so it is
                hidden whole and said once as a sentence (cited-errors §2). */}
            <p className="text-sm">
              <span className="sr-only">{t('cited-said-better', { said: e.learnerForm, better: e.targetForm })}</span>
              <span aria-hidden lang="en" dir="ltr">
                <s>{e.learnerForm}</s> → {e.targetForm}
              </span>
            </p>
            <span className="badge badge-ghost badge-sm shrink-0">{severityLabel(e.severity)}</span>
          </div>
          {/* English beside a translated debrief too: extraction writes it
              for a teacher and is unchanged (spec §3.1, §11). */}
          {e.explanation && (
            <p className="text-base-content/60 text-xs">
              <En>{e.explanation}</En>
            </p>
          )}
          <HearIt target={{ errorRecordId: e.id }} label={t('cited-hear', { line: preview(e.targetForm) })} />
        </li>
      ))}
    </ul>
  );
}
